"use client";
import { Box, CircularProgress } from "@mui/material";
import { AuthenticatedTemplate, useMsal } from "@azure/msal-react";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise/styles/ag-grid.css"; // Core CSS
import "ag-grid-enterprise/styles/ag-theme-quartz.css"; // Theme
import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { redirect } from "next/navigation";
import { AuthenticatedContext, AuthorizationContext, TOKEN_SESSION_CHECK_INTERVAL, TOKEN_EXPIRY_SESSION_BUFFER } from "@components/AuthorizationContext";
import ProtectedResource from "@components/ProtectedResource";
import SideNav from "@components/SideNav/SideNav";
import { AdminRole, AllRoles } from "@lib/authorizationRoles";
import { auditLogIn, fetchClientServicesOfAUser, fetchGroupsOfAUser } from "@lib/users/user-client-service";
import { LayoutProps } from "@lib/definitions";
import { getTokenAccess } from "@utils/callApiService";
import { checkRole, checkRoleAdmin, getTokenExpirationTime } from "@utils/helpers";
import AppHeader from "@components/Layout/AppHeader";
import AppFooter from "@components/Layout/AppFooter";
import { AuthenticationResult } from "@azure/msal-browser";
import { getLoginRoute } from "@utils/routeUtils";
import mainLayoutReducer, { MainLayoutActionType, MainLayoutState } from "@reducers/mainLayoutReducer";

LicenseManager.setLicenseKey(process?.env?.NEXT_PUBLIC_AG_GRID_LICENSE_KEY ?? "");

export default function MainLayout({ children }: Readonly<LayoutProps>) {
	const { instance, accounts } = useMsal();
	const [isLoading, setIsLoading] = useState(true);
	const isInitial = useRef(true);
	const initState = { authenticatedContext: {} } as MainLayoutState;
	const [open, setOpen] = React.useState(false);

	const [layoutState, dispatch] = useReducer(mainLayoutReducer, initState);

	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const setApiTokenResult = useCallback((apiToken: AuthenticationResult) => {
		dispatch({
			type: MainLayoutActionType.UPDATE_TOKEN,
			apiTokenResult: apiToken
		});
	}, [layoutState.authenticatedContext]);

	useEffect(() => {
		//If access token is changed, add interval to check token expiration to navigate to login if it expired
		const checkSession = () => {
			try {
				const expiredTime = getTokenExpirationTime();
				if (!expiredTime || expiredTime <= 0) {
					return;
				}

				console.info("Checking login session. Time left (milliseconds):", expiredTime - new Date().valueOf()); //TODO: Remove after testing
				if ((expiredTime - new Date().valueOf()) <= TOKEN_EXPIRY_SESSION_BUFFER) {
					redirectToLogin();
				}
			} catch (error) {
				console.error("Invalid token:", error);
				redirectToLogin();
			}
		};

		const redirectToLogin = () => {
			if (typeof window === "undefined") {
				return;
			}

			console.info("Redirect to login page due to session expired");

			layoutState.authenticatedContext?.tryLogout?.();
			window.onbeforeunload = null;
			window.location.href = getLoginRoute(true);
		};

		//Prevent multiple intervals
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		//Set new interval
		intervalRef.current = setInterval(checkSession, TOKEN_SESSION_CHECK_INTERVAL);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};

	}, [layoutState.authenticatedContext]);

	useEffect(() => {
		const activeAccount = instance.getActiveAccount();
		if (activeAccount && layoutState.authenticatedContext?.userGroups === undefined && isInitial.current) {
			const fetchUserGroups = async () => {
				const apiTokenResult = await getTokenAccess(instance, activeAccount, true);
				if (apiTokenResult === undefined || apiTokenResult === null) {
					return;
				}

				const promises = [
					auditLogIn(apiTokenResult, activeAccount.localAccountId),
					fetchGroupsOfAUser(apiTokenResult, activeAccount.localAccountId)
				];

				if (activeAccount.tenantId !== process.env.NEXT_PUBLIC_AAD_TENANT_ID!) {
					promises.push(fetchClientServicesOfAUser(apiTokenResult, activeAccount.localAccountId));
				}

				const results = await Promise.all(promises);

				const groupResult = results[1];

				let clientServices = [];

				if (results.length === 3) {
					clientServices = results[2];
				}

				const isAdminRole = checkRoleAdmin(groupResult, AdminRole);
				dispatch({
					type: MainLayoutActionType.INIT_AUTHENTICATION_CONTEXT,
					authenticatedContext: new AuthenticatedContext(
						instance,
						apiTokenResult,
						activeAccount,
						groupResult,
						isAdminRole,
						activeAccount.tenantId === process.env.NEXT_PUBLIC_AAD_TENANT_ID!,
						isAdminRole && activeAccount.tenantId === process.env.NEXT_PUBLIC_AAD_TENANT_ID!,
						clientServices,
						setApiTokenResult,
					)
				});

				setIsLoading(false);
			}
			fetchUserGroups();
			isInitial.current = false;
		}
		else if (activeAccount && layoutState.authenticatedContext?.userGroups && layoutState.authenticatedContext?.refreshToken) {
			console.info("Refreshing token");
			layoutState.authenticatedContext.refreshToken();
			setIsLoading(false);
		}
	}, [instance, accounts, layoutState.authenticatedContext]);

	useEffect(() => {
		if (!isLoading && !checkRole(layoutState.authenticatedContext, AllRoles)) {
			redirect("/unauthorized");
		}
	}, [isLoading, layoutState.authenticatedContext])

	return <ProtectedResource> {
		isLoading ? (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					height: "100%",
				}}>
				<CircularProgress />
			</Box>
		) : (
			<Box sx={{display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
				{layoutState.authenticatedContext
					&& layoutState.authenticatedContext.refreshToken != undefined
					&& layoutState.authenticatedContext.userGroups?.length > 0
					&&
					<AuthorizationContext.Provider value={layoutState.authenticatedContext} >
						<Box sx={{ display: 'flex', flexGrow: 1 }}>
							<SideNav  open={open} openCallback={(open: boolean) => setOpen(open)} />
							<Box sx={{
								flexGrow: 1,
								display: 'flex',
								flexDirection: 'column'
							}}>
								<Box component='main' sx={{ flexGrow: 1 }}>
									<AuthenticatedTemplate>
										{children}
										<AppFooter></AppFooter>
									</AuthenticatedTemplate>
								</Box>
							</Box>
						</Box>
					</AuthorizationContext.Provider>
				}
			</Box>)
	}
	</ProtectedResource>
}